import { useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import authService from '../../services/auth.service';
import { useActiveMaintenance } from '../../hooks/useActiveMaintenance';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { isLanguage, showIn } from '../../i18n/language';
import { dashboardPathForRole } from '../../lib/roleRouting';
import { colors, gradients, shadows } from '../../styles/theme';
import BrandMark from './BrandMark';
import GlobalSearch from './GlobalSearch';
import LayoutRealtimeBus from './LayoutRealtimeBus';
import { RealtimeProvider } from './RealtimeProvider';
import TopNav from './TopNav';
import UserMenu from './UserMenu';
import EmailVerificationBanner from '../client/EmailVerificationBanner';
import MaintenanceBanner from '../maintenance/MaintenanceBanner';
import MaintenancePreModal from '../maintenance/MaintenancePreModal';

/**
 * Authenticated app shell.
 *
 * Chrome (topbar) is navy + gold to mirror the auth screens — the user
 * never perceives a brand break between login and the workspace. The
 * canvas underneath stays neutral white so financial tables and KPIs
 * read with maximum legibility (no gradient fatigue in long sessions).
 *
 * Top-nav over sidebar is deliberate: institutional investment platforms
 * (Aladdin, Bloomberg, Schwab) prioritise horizontal screen real-estate
 * for tabular data over the SaaS sidebar pattern.
 */
const MainLayout = (): JSX.Element => {
  const { user, logout } = useAuthStore();
  const home = dashboardPathForRole(user?.role);
  const navigate = useNavigate();

  // Single source of truth for the active maintenance window: the banner
  // and the force-logout effect read the same hook result, so we don't
  // pay for two pollers.
  const maintenance = useActiveMaintenance(true);
  const firedLogoutRef = useRef(false);

  // The account says which language its owner reads in, on whichever device: a browser
  // that remembers another one is brought in line once, when the profile arrives.
  const accountLanguage = useCurrentUserProfile()?.language;
  useEffect(() => {
    if (isLanguage(accountLanguage)) showIn(accountLanguage);
  }, [accountLanguage]);

  useEffect(() => {
    if (!maintenance.startedNow || firedLogoutRef.current) return;
    firedLogoutRef.current = true;
    // Wipe both axios-side localStorage tokens and the Zustand auth
    // store before redirecting — otherwise a fast in-flight request
    // could repopulate state mid-navigation.
    authService.logout();
    logout();
    navigate('/maintenance', { replace: true });
  }, [maintenance.startedNow, logout, navigate]);

  return (
    // RealtimeProvider owns the single SSE connection for the
    // authenticated session. Page components register their widget
    // refetchers + event handlers via `useRegisterWidget` /
    // `useRegisterEventHandler` (see RealtimeProvider.tsx) instead
    // of opening their own EventSource.
    <RealtimeProvider>
      <div className="min-h-screen flex flex-col" style={{ background: colors.canvas }}>
        {/* 15-minute pre-maintenance warning (#54) — overlay, authenticated only. */}
        <MaintenancePreModal data={maintenance} />
        {/* Banner + header share a single sticky container so the
            banner sits ABOVE the NavBar and both scroll together. */}
        <div className="sticky top-0 z-40">
          <MaintenanceBanner data={maintenance} />
          <header
            style={{
              background: gradients.navy,
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              boxShadow: shadows.topbar,
            }}
          >
            <div className="relative max-w-[1440px] mx-auto px-6 h-14 flex items-center justify-between gap-6">
              <div className="flex flex-1 min-w-0 items-center gap-4 lg:gap-8">
                <Link to={home} className="flex items-center" aria-label="QMI Investor Portal home">
                  <BrandMark />
                </Link>
                <TopNav role={user?.role} />
              </div>
              {user?.role !== 'client' && <GlobalSearch role={user?.role ?? ''} />}
              <UserMenu />
            </div>
          </header>
        </div>

        {/* Session-wide `appointment.imminent` reminder. Subscribes
            via the RealtimeProvider context — no separate stream. */}
        <LayoutRealtimeBus />

        <main className="flex-1">
          <div className="max-w-[1440px] mx-auto px-6 py-8">
            {user?.role === 'client' && <EmailVerificationBanner />}
            <Outlet />
          </div>
        </main>
      </div>
    </RealtimeProvider>
  );
};

export default MainLayout;
